
import { ArrowUpRight, Mail, MapPin, LayoutDashboard, FileText, Download, Book, List, CheckSquare, Globe, Phone, Star, BadgeCheck, Lightbulb, Trophy, BrainCircuit, Clock, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sendNewsletterSignup } from "@/utils/emailService";
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface NavItem {
  name: string;
  path: string;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  external?: boolean;
}

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [timeLeft, setTimeLeft] = useState<{days: number, hours: number, minutes: number, seconds: number}>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Calculate countdown to the last day of the current month
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      const difference = lastDayOfMonth.getTime() - now.getTime();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setSubscribing(true);
    
    try {
      const success = await sendNewsletterSignup({ email });
      
      if (success) {
        setEmail("");
        toast({
          title: "Subscribed Successfully!",
          description: "Thanks for subscribing to our newsletter.",
        });
      } else {
        toast({
          title: "Subscription Failed",
          description: "There was an error subscribing you to our newsletter. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Subscription Error",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubscribing(false);
    }
  };

  const mainNavItems: NavItem[] = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Case Studies", path: "/case-studies" },
    { name: "Neighborhoods", path: "/neighborhoods", icon: <MapPin className="w-3.5 h-3.5 mr-1" /> },
    { name: "Knowledge Base", path: "/knowledge-base", icon: <Book className="w-3.5 h-3.5 mr-1" /> },
    { name: "SEO Tool", path: "/seo-tool", icon: <Globe className="w-3.5 h-3.5 mr-1" /> },
    { name: "SEO Quiz", path: "/seo-quiz", icon: <Trophy className="w-3.5 h-3.5 mr-1" /> },
    { name: "Pricing", path: "/pricing", icon: <FileText className="w-3.5 h-3.5 mr-1" /> },
    { name: "Contact", path: "/contact", icon: <Mail className="w-3.5 h-3.5 mr-1" /> },
  ];

  const legalNavItems: NavItem[] = [
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms of Service", path: "/terms-of-service" },
    { 
      name: "Dashboard", 
      path: "/dashboard", 
      icon: <LayoutDashboard className="w-3.5 h-3.5" /> 
    },
    { 
      name: "Sitemap", 
      path: "/sitemap.xml", 
      icon: <FileText className="w-3.5 h-3.5" />,
      className: "text-muted-foreground",
      external: true
    },
  ];

  const resourceItems: NavItem[] = [
    { 
      name: "Complete SEO Checklist", 
      path: "/resources/seo-checklist", 
      icon: <CheckSquare className="w-3.5 h-3.5" />,
      className: "group-hover:text-primary" 
    },
    { 
      name: "Backlink Building 101", 
      path: "/resources/backlink-101", 
      icon: <Globe className="w-3.5 h-3.5" />,
      className: "group-hover:text-primary" 
    },
    { 
      name: "New Website SEO SOP", 
      path: "/resources/website-seo-sop", 
      icon: <List className="w-3.5 h-3.5" />,
      className: "group-hover:text-primary" 
    },
    { 
      name: "On-Page SEO Essentials (PDF)", 
      path: "/resources/on-page-seo-essentials.pdf", 
      icon: <Download className="w-3.5 h-3.5" />,
      className: "group-hover:text-primary" 
    },
    { 
      name: "SEO Audit Template", 
      path: "/resources/seo-audit-template.xlsx", 
      icon: <Download className="w-3.5 h-3.5" />,
      className: "group-hover:text-primary" 
    },
    { 
      name: "Choosing a Marketing Partner in LA", 
      path: "/resources/choosing-digital-marketing-partner-los-angeles", 
      icon: <Book className="w-3.5 h-3.5" />,
      className: "group-hover:text-primary" 
    },
  ];
  
  // Footer animation variables
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <footer className="bg-gradient-to-b from-background to-muted/30 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-16 -left-16 w-32 h-32 bg-indigo-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl"></div>
        
        {/* Stars decoration */}
        {Array.from({ length: 5 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            initial={{ scale: 0.6, opacity: 0.7 }}
            animate={{ 
              scale: [0.6, 0.8, 0.6],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{ 
              duration: 3 + i,
              repeat: Infinity,
              repeatType: "reverse",
              delay: i * 0.5
            }}
            style={{
              top: `${10 + Math.random() * 80}%`,
              left: `${5 + Math.random() * 90}%`,
            }}
          >
            <Star className="w-3 h-3 text-amber-400/40" fill="currentColor" />
          </motion.div>
        ))}
      </div>

      <div className="section-padding pt-12 pb-8 relative z-10">
        <div className="page-container">
          <motion.div 
            className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-12 lg:gap-10"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Company Info */}
            <motion.div 
              className="lg:col-span-4"
              variants={itemVariants}
            >
              <div className="bg-card/60 backdrop-blur-sm p-6 rounded-xl border border-border/50 h-full flex flex-col shadow-soft">
                <h3 className="font-bold text-lg mb-4 flex items-center">
                  <BadgeCheck className="w-5 h-5 text-desert-sunset mr-2" />
                  Los Angeles SEO Group
                </h3>
                <p className="text-sm text-foreground/80 mb-6 leading-relaxed">
                  Your trusted partner for SEO services in Los Angeles. We help
                  businesses grow their online presence and reach more customers.
                </p>
                
                <div className="space-y-4 flex-grow">
                  <div className="flex items-start gap-3">
                    <div className="bg-muted rounded-full p-2 shrink-0">
                      <Mail className="w-4 h-4 text-desert-canyon" />
                    </div>
                    <div className="pt-1">
                      <p className="text-sm text-foreground/80 break-all hover:text-desert-sunset transition-colors">
                        sales@losangelesseogroup.com
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-muted rounded-full p-2 shrink-0">
                      <Phone className="w-4 h-4 text-desert-sky" />
                    </div>
                    <div className="pt-1">
                      <a href="tel:+13237841577" className="text-sm text-foreground/80 hover:text-desert-sky transition-colors">
                        (323) 784-1577
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-muted rounded-full p-2 shrink-0">
                      <MapPin className="w-4 h-4 text-desert-cactus" />
                    </div>
                    <div className="pt-1">
                      <p className="text-sm text-foreground/80">
                        10000 Washington Blvd, Culver City, CA 90232
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-4 mt-6">
                  <a
                    href="#"
                    className="bg-muted/70 hover:bg-desert-sunset/10 hover:text-desert-sunset p-2 rounded-full transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.19795 21.5H13.198V13.4901H16.8021L17.198 9.50977H13.198V7.5C13.198 6.94772 13.6457 6.5 14.198 6.5H17.198V2.5H14.198C11.4365 2.5 9.19795 4.73858 9.19795 7.5V9.50977H7.19795L6.80206 13.4901H9.19795V21.5Z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    className="bg-muted/70 hover:bg-desert-sky/10 hover:text-desert-sky p-2 rounded-full transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    className="bg-muted/70 hover:bg-primary/10 hover:text-primary p-2 rounded-full transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.5 8C7.32843 8 8 7.32843 8 6.5C8 5.67157 7.32843 5 6.5 5C5.67157 5 5 5.67157 5 6.5C5 7.32843 5.67157 8 6.5 8Z" />
                      <path d="M5 10C5 9.44772 5.44772 9 6 9H7C7.55228 9 8 9.44772 8 10V18C8 18.5523 7.55228 19 7 19H6C5.44772 19 5 18.5523 5 18V10Z" />
                      <path d="M11 19H12C12.5523 19 13 18.5523 13 18V14.5C13 13.9477 13.4477 13.5 14 13.5C14.5523 13.5 15 13.9477 15 14.5V18C15 18.5523 15.4477 19 16 19H17C17.5523 19 18 18.5523 18 18V13.5C18 11.567 16.433 10 14.5 10C13.2744 10 12.1946 10.6321 11.6713 11.6094C11.5297 11.8398 11.4044 12.0865 11.2984 12.3438C11.1087 12.8443 10.5523 13 10 13H10C9.44772 13 9 12.5523 9 12V10C9 9.44772 9.44771 9 10 9H11C11.5523 9 12 9.44772 12 10V10.3369C12 10.7963 12.3969 11.1452 12.8237 10.9917C13.3131 10.8131 13.863 10.6074 14.5 10.5C16.433 10.5 18 12.067 18 14V18C18 18.5523 18.4477 19 19 19H20C20.5523 19 21 18.5523 21 18V14C21 10.4101 18.0899 7.5 14.5 7.5C13.337 7.5 12.2385 7.77393 11.2571 8.26361" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>
            
            {/* Site Navigation */}
            <motion.div 
              className="lg:col-span-2 md:col-span-1"
              variants={itemVariants}
            >
              <div className="h-full">
                <h3 className="font-bold text-lg mb-4 text-desert-sunset">Main Navigation</h3>
                <ul className="space-y-2.5">
                  {mainNavItems.map((item, index) => (
                    <li key={index}>
                      <Link 
                        to={item.path}
                        className="flex items-center text-sm text-foreground/70 hover:text-desert-sunset transition-colors"
                      >
                        {item.icon}
                        <span>{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
            
            {/* Resources */}
            <motion.div 
              className="lg:col-span-3 md:col-span-1"
              variants={itemVariants}
            >
              <div className="h-full">
                <h3 className="font-bold text-lg mb-4 text-desert-sky">Free Resources</h3>
                <ul className="space-y-2.5">
                  {resourceItems.map((item, index) => (
                    <li key={index}>
                      <Link 
                        to={item.path}
                        className="flex items-center text-sm text-foreground/70 hover:text-desert-sky transition-colors group"
                      >
                        <span className={`inline-flex mr-2 ${item.className || ''}`}>
                          {item.icon}
                        </span>
                        <span>{item.name}</span>
                        {item.external && (
                          <ArrowUpRight className="w-3 h-3 ml-1 text-muted-foreground" />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
            
            {/* Newsletter */}
            <motion.div 
              className="lg:col-span-3 md:col-span-1"
              variants={itemVariants}
            >
              <div className="bg-muted/30 p-5 rounded-lg border border-border/50 shadow-soft h-full">
                <h3 className="font-bold text-lg mb-2 text-desert-canyon">SEO Newsletter</h3>
                <p className="text-xs text-foreground/70 mb-3">
                  Get the latest SEO tips and strategies delivered to your inbox.
                </p>
                
                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="text-sm"
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-desert-sunset hover:bg-desert-sunset/90 text-white"
                    disabled={subscribing}
                  >
                    {subscribing ? "Subscribing..." : "Subscribe"}
                  </Button>
                </form>
                
                <div className="mt-4 pt-3 border-t border-border/40">
                  <p className="text-xs font-medium mb-2">
                    Limited time offer - expires in:
                  </p>
                  <div className="grid grid-cols-4 gap-1 text-center">
                    {[
                      { value: timeLeft.days, label: "Days", color: "bg-desert-sunset/10 text-desert-sunset" },
                      { value: timeLeft.hours, label: "Hours", color: "bg-desert-canyon/10 text-desert-canyon" },
                      { value: timeLeft.minutes, label: "Mins", color: "bg-desert-sky/10 text-desert-sky" },
                      { value: timeLeft.seconds, label: "Secs", color: "bg-desert-cactus/10 text-desert-cactus" }
                    ].map((item, i) => (
                      <div key={i} className={`p-1.5 rounded-sm ${item.color}`}>
                        <div className="text-base font-bold">{item.value}</div>
                        <div className="text-xs opacity-80">{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Bottom bar with copyright and legal links */}
          <div className="mt-12 pt-4 border-t border-border/20 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-muted-foreground order-2 md:order-1">
              © {new Date().getFullYear()} Los Angeles SEO Group. All rights reserved.
            </div>
            
            <ul className="flex flex-wrap items-center gap-5 order-1 md:order-2">
              {legalNavItems.map((item, index) => (
                <li key={index}>
                  {item.external ? (
                    <a
                      href={item.path}
                      className={`text-xs flex items-center gap-1 ${item.className || 'text-muted-foreground hover:text-foreground'} transition-colors`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.icon}
                      <span>{item.name}</span>
                      <ArrowUpRight className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={`text-xs flex items-center gap-1 ${item.className || 'text-muted-foreground hover:text-foreground'} transition-colors`}
                    >
                      {item.icon}
                      <span>{item.name}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
